import { useDisclosure } from '@chakra-ui/react';
import { ICustomFieldRecordSchema } from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { Fragment } from 'react';
import {
  MButton,
  MGrid,
  MLockedTextOrContent,
  MText,
} from '../../../../../components/Monetize';
import MPopoverWrapper from '../../../../../components/Monetize/MPopoverWrapper';
import { useQuoteOfferingGroupCustomFields } from '../../../../../hooks/useQuoteOfferingGroupCustomFields';
import { getCustomFieldDisplayValue } from '../../../../../utils/customFields';
import { GroupHeaderDivider } from './QuoteOfferingGroupHeaderShell';

interface QuoteOfferingGroupCustomFieldsPopoverProps {
  /** Values stored on the group — sparse; every configured field is listed */
  customFields: Maybe<ICustomFieldRecordSchema>;
  /** Names the trigger, e.g. "Group 2" */
  groupLabel: string;
  testId: string;
}

/**
 * Peek at a group's additional (custom) field values from the quote's offering
 * table, so they can be checked without opening Manage Groups.
 *
 * Renders nothing at all — its own separator included — when the tenant has no
 * QUOTE_OFFERING_GROUP custom fields configured. A control that always opens
 * onto "no fields" is noise in a header strip that is already dense.
 */
export const QuoteOfferingGroupCustomFieldsPopover = ({
  customFields,
  groupLabel,
  testId,
}: QuoteOfferingGroupCustomFieldsPopoverProps) => {
  const { open, onOpen, onClose } = useDisclosure();
  const { customFields: configuredFields, hasCustomFields } =
    useQuoteOfferingGroupCustomFields();

  if (!hasCustomFields) {
    return null;
  }

  return (
    <>
      <GroupHeaderDivider />
      <MPopoverWrapper
        showArrow
        usePortal
        placement="bottom-start"
        isOpen={open}
        onOpen={onOpen}
        onClose={onClose}
        popoverContentProps={{ width: '20rem' }}
        triggerContent={
          /*
           * The same link-styled trigger the record lookup popovers use, so
           * "text in a header strip that opens a detail popover" looks the same
           * wherever it appears. The accessible name is qualified by the group
           * because a quote renders one of these per group container.
           */
          <MButton
            variant="link"
            size="sm"
            whiteSpace="nowrap"
            flexShrink={0}
            aria-label={`Additional Fields for ${groupLabel}`}
            onClick={onOpen}
            data-testid={`${testId}-custom-fields-btn`}
          >
            Additional Fields
          </MButton>
        }
        headerContent={
          <MText fontSize="sm" fontWeight="semibold">
            Additional Fields
          </MText>
        }
        bodyContent={
          <MGrid
            templateColumns="minmax(0, 1fr) minmax(0, 1fr)"
            columnGap={3}
            rowGap={2}
            data-testid={`${testId}-custom-fields`}
          >
            {configuredFields.map(({ key, displayLabel, type }) => (
              <Fragment key={key}>
                <MText fontSize="sm" color="fg.muted">
                  {displayLabel}
                </MText>
                {/*
                 * Renders the app's standard italic "Blank" for a field the
                 * group has no value for. `minH` is unset because the default
                 * is sized for a form row, not a dense list.
                 */}
                <MLockedTextOrContent
                  isLocked
                  text={getCustomFieldDisplayValue(type, customFields?.[key])}
                  textProps={{ fontSize: 'sm', wordBreak: 'break-word' }}
                  minH={0}
                />
              </Fragment>
            ))}
          </MGrid>
        }
      />
    </>
  );
};
